import React, { useState } from "react";
import { ApolloError, gql, useLazyQuery, useMutation } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { Modal } from "src/components/modal/modal";
import { Body, PageTitle } from "src/components/typography/typography";
import { Report } from "src/generated/types";
import { parseError } from "src/utils/parse_error";
import { Routes } from "../../../utils/routes";

import { AdministratorsHelp, SupportStaffHelp } from "./role_help";

/*
 * GraphQL
 */

export const UPDATE_CONSTITUENT_GROUPS_MUTATION = gql`
    mutation UpdateConstituentGroups($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

const CURRENT_REPORT_QUERY = gql`
    query DashboardData {
        viewer {
            user {
                id
                school {
                    id
                    currentReport {
                        id
                        promotionProgress
                        percentCompleted
                        status
                        submissionProgress
                        surveysCompleted
                        constituentGroups
                    }
                    currentReports {
                        edges {
                            node {
                                id
                                promotionProgress
                                percentCompleted
                                status
                                submissionProgress
                                surveysCompleted
                                constituentGroups
                            }
                        }
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ConstituentGroupsFormProps {
    reports: Report[];
    schoolId?: string;
}

interface FormValues {
    administrators: string;
    alumni: string;
    boardMembers: string;
    parents: string;
    sixthGradeStudents: string;
    eighthGradeStudents: string;
    supportStaff: string;
    teachers: string;
}

/*
 * Main
 */

export const ConstituentGroupsCombinedForm = (props: ConstituentGroupsFormProps): React.ReactElement => {
    const history = useHistory();
    const { reports, schoolId } = props;

    const fsciReport = reports.find((report) => report?.type === "fsci");
    const ffiReport = reports.find((report) => report?.type === "ffi");

    if (!fsciReport || !ffiReport) {
        return (
            <div className="no-reports-container">
                <PageTitle>No Reports</PageTitle>
                <Body>
                    You have no reports available to submit. Please contact your administrator if you believe this is an
                    error.
                </Body>
            </div>
        );
    }

    const [isAdministratorsModalOpen, setAdministratorsModalOpen] = useState(false);
    const [isAlumniModalOpen, setAlumniModalOpen] = useState(false);
    const [isBoardMembersModalOpen, setBoardMembersModalOpen] = useState(false);
    const [isParentsModalOpen, setParentsModalOpen] = useState(false);
    const [isStudentModalOpen, setStudentModalOpen] = useState(false);
    const [isSupportStaffModalOpen, setSupportStaffModalOpen] = useState(false);
    const [isTeachersModalOpen, setTeachersModalOpen] = useState(false);
    const [studentModelGradeLevel, setStudentModelGradeLevel] = useState(6);
    const [updateConstituentGroups] = useMutation(UPDATE_CONSTITUENT_GROUPS_MUTATION);
    const [refreshReport] = useLazyQuery(CURRENT_REPORT_QUERY, {
        fetchPolicy: "network-only",
    });

    const initialValues = {
        administrators: fsciReport.constituentGroups.administrators ?? 0,
        alumni: fsciReport.constituentGroups.alumni ?? 0,
        boardMembers: fsciReport.constituentGroups.boardMembers ?? 0,
        parents: fsciReport.constituentGroups.parents ?? 0,
        sixthGradeStudents: fsciReport.constituentGroups.students ?? 0,
        eighthGradeStudents: ffiReport.constituentGroups.students ?? 0,
        supportStaff: fsciReport.constituentGroups.supportStaff ?? 0,
        teachers: fsciReport.constituentGroups.teachers ?? 0,
    };

    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }): void => {
                    const constituentGroups = {
                        fsci: {
                            administrators: parseInt(values.administrators, 10),
                            alumni: parseInt(values.alumni, 10),
                            boardMembers: parseInt(values.boardMembers, 10),
                            parents: parseInt(values.parents, 10),
                            students: parseInt(values.sixthGradeStudents, 10),
                            supportStaff: parseInt(values.supportStaff, 10),
                            teachers: parseInt(values.teachers, 10),
                        },
                        ffi: {
                            administrators: parseInt(values.administrators, 10),
                            alumni: parseInt(values.alumni, 10),
                            boardMembers: parseInt(values.boardMembers, 10),
                            parents: parseInt(values.parents, 10),
                            students: parseInt(values.eighthGradeStudents, 10),
                            supportStaff: parseInt(values.supportStaff, 10),
                            teachers: parseInt(values.teachers, 10),
                        },
                    };

                    updateConstituentGroups({
                        variables: {
                            input: {
                                functionName: "updateConstituentGroupsForCurrentReports",
                                params: {
                                    schoolId,
                                    constituentGroups,
                                },
                            },
                        },
                    })
                        .then(() => {
                            refreshReport()
                                .then(() => {
                                    history.push(Routes.promotion);
                                    toast.success("Constituent groups successfully updated.");
                                })
                                .catch((error: ApolloError) => {
                                    toast.error(parseError(error));
                                });
                        })
                        .catch((error: ApolloError) => {
                            toast.error(parseError(error));
                        });

                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape({
                    administrators: Yup.string().required("Required"),
                    alumni: Yup.string().required("Required"),
                    boardMembers: Yup.string().required("Required"),
                    parents: Yup.string().required("Required"),
                    sixthGradeStudents: Yup.string().required("Required"),
                    eighthGradeStudents: Yup.string().required("Required"),
                    supportStaff: Yup.string().required("Required"),
                    teachers: Yup.string().required("Required"),
                })}
            >
                {(formik: FormikProps<FormValues>) => (
                    <Form>
                        <div className="form-main-container">
                            <Modal isOpen={isStudentModalOpen} setOpen={setStudentModalOpen} title="Students">
                                <Body>
                                    {studentModelGradeLevel === 6 && (
                                        <>
                                            Enter the TOTAL number of students enrolled in grade 6 and up. This includes
                                            all relevant full-time, part-time, or distance students. If your school has
                                            only students up through 5th grade, enter ZERO (0). This value is used to
                                            calculate the response rate for your FSCI report.
                                        </>
                                    )}
                                    {studentModelGradeLevel === 8 && (
                                        <>
                                            Enter the TOTAL number of students enrolled in grade 8 and up. This includes
                                            all relevant full-time, part-time, or distance students. If your school has
                                            only students up through 7th grade, enter ZERO (0). This value is used to
                                            calculate the response rate for your FFI report.
                                        </>
                                    )}
                                </Body>
                            </Modal>
                            <h2 className="constituent-groups-subtitle">Students</h2>
                            <div className="form-group">
                                <Input
                                    id="sixthGradeStudents"
                                    label="6th Grade and Above (FSCI Reports)"
                                    name="sixthGradeStudents"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setStudentModelGradeLevel(6);
                                                        setStudentModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                                <Input
                                    id="eighthGradeStudents"
                                    label="8th Grade and Above (FFI Reports)"
                                    name="eighthGradeStudents"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setStudentModelGradeLevel(8);
                                                        setStudentModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                            </div>
                            <h2 className="constituent-groups-subtitle">School Staff</h2>
                            <div className="form-group">
                                <Input
                                    id="teachers"
                                    label="Teachers"
                                    name="teachers"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setTeachersModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                                <Modal
                                                    isOpen={isTeachersModalOpen}
                                                    setOpen={setTeachersModalOpen}
                                                    title="Teachers"
                                                >
                                                    <Body>
                                                        As teachers have a unique view across internal and family
                                                        stakeholders engaged with the school, the teacher questionnaire
                                                        is slightly longer than questionnaires presented to other
                                                        groups.
                                                    </Body>
                                                    <Body>
                                                        The median time to complete the FSCI teacher questionnaire is 7
                                                        minutes and 40 seconds.
                                                    </Body>
                                                </Modal>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                                <Input
                                    id="administrators"
                                    label="Administrators"
                                    name="administrators"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setAdministratorsModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                                <Modal
                                                    isOpen={isAdministratorsModalOpen}
                                                    setOpen={setAdministratorsModalOpen}
                                                    title="Administrators"
                                                >
                                                    <AdministratorsHelp />
                                                </Modal>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                                <Input
                                    id="supportStaff"
                                    label="Support Staff"
                                    name="supportStaff"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setSupportStaffModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                                <Modal
                                                    isOpen={isSupportStaffModalOpen}
                                                    setOpen={setSupportStaffModalOpen}
                                                    title="Support Staff"
                                                >
                                                    <SupportStaffHelp />
                                                </Modal>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                            </div>
                            <h2 className="constituent-groups-subtitle">Other</h2>
                            <div className="form-group">
                                <Input
                                    id="parents"
                                    label="Parents"
                                    name="parents"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setParentsModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                                <Modal
                                                    isOpen={isParentsModalOpen}
                                                    setOpen={setParentsModalOpen}
                                                    title="Parents"
                                                >
                                                    <Body>
                                                        Enter the TOTAL number of parents and legal guardians. If
                                                        distributing primarily or exclusively via email, enter the total
                                                        number of valid, current parent/guardian email addresses you
                                                        will be contacting (size of your parent contact list).
                                                    </Body>
                                                </Modal>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                                <Input
                                    id="alumni"
                                    label="Alumni"
                                    name="alumni"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setAlumniModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                                <Modal
                                                    isOpen={isAlumniModalOpen}
                                                    setOpen={setAlumniModalOpen}
                                                    title="Alumni"
                                                >
                                                    <Body>
                                                        Enter the TOTAL number of alumni of the school with whom you
                                                        have the ability to contact.
                                                    </Body>
                                                    <Body>
                                                        If distributing primarily or exclusively via email, enter the
                                                        total number of valid alumni email addresses you will be
                                                        contacting. If distributing primarily through social media
                                                        channels (e.g. Facebook), estimate the rough number of your
                                                        active followers who are alumni. If sending alumni links via
                                                        multiple channels, add the total numbers and estimates across
                                                        all channels being used.
                                                    </Body>
                                                </Modal>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                                <Input
                                    id="boardMembers"
                                    label="Board Members"
                                    name="boardMembers"
                                    placeholder="0"
                                    renderLabelMeta={() => {
                                        return (
                                            <div className="modal-link-container">
                                                <span
                                                    className="whats-this-text"
                                                    onClick={(): void => {
                                                        setBoardMembersModalOpen(true);
                                                    }}
                                                >
                                                    What's this?
                                                </span>
                                                <Modal
                                                    isOpen={isBoardMembersModalOpen}
                                                    setOpen={setBoardMembersModalOpen}
                                                    title="Board Members"
                                                >
                                                    <Body>
                                                        Enter the TOTAL number of currently active board members for the
                                                        school. If your school has no board of directors or board
                                                        members, enter ZERO (0).
                                                    </Body>
                                                </Modal>
                                            </div>
                                        );
                                    }}
                                    type="text"
                                />
                            </div>
                            <div className="form-group">
                                <Button
                                    disabled={formik.isSubmitting || !formik.isValid}
                                    type="submit"
                                    variation="primary"
                                >
                                    Submit Constituent Groups
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};
